import { homeTypes } from "@app/shared/swap-request/static-data/home-types.constant";
import { floorOptions } from "@app/shared/swap-request/static-data/floors-options.constant";
import { homeFeatures } from "@app/shared/swap-request/static-data/home-features.constant";
import { EPropertyType, EFloorType } from "@app/shared/swap-request/enums/swap-request.enum";
import { ISwapRequestEntity, IProperty } from "@app/shared/swap-request/interfaces/swap-request.interface";

/* ----- Relative time keys ----- */
export function getRelativeTimeKey(updatedAt: string | Date | undefined): { key: string; value?: number } {
  const updatedDate = new Date(Number(updatedAt));
  if (isNaN(updatedDate.getTime()) || Date.now() - updatedDate.getTime() < 0) {
    return { key: "relativeTime.recentlyUpdated", value: 0 };
  }

  const diffInSeconds = Math.floor((Date.now() - updatedDate.getTime()) / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInYears > 0) return { key: "relativeTime.yearsAgo", value: diffInYears };
  if (diffInMonths > 0) return { key: "relativeTime.monthsAgo", value: diffInMonths };
  if (diffInDays > 0) return { key: "relativeTime.daysAgo", value: diffInDays };
  if (diffInHours > 0) return { key: "relativeTime.hoursAgo", value: diffInHours };
  if (diffInMinutes > 0) return { key: "relativeTime.minutesAgo", value: diffInMinutes };

  return { key: "relativeTime.justUpdated", value: 0 };
}

/* ----- Property / Floor type ----- */
export function getPropertyTypeTranslationKey(propertyType: EPropertyType): string {
  const homeType = homeTypes.find(ht => ht.id === propertyType);
  return homeType ? homeType.label : propertyType;
}

export function getFloorTypeTranslationKey(floorType: EFloorType): string {
  const floorOption = floorOptions.find(fo => fo.value === floorType);
  return floorOption ? floorOption.label : floorType;
}

/* ----- SwapRequest tags ----- */
export function generateSpecificTags(swapRequest: ISwapRequestEntity) {
  const tags: { label: string; style: string }[] = [];
  if (swapRequest.bestMatch) tags.push({ label: "tags.bestMatch", style: "best-match" });
  if (swapRequest.likedMe) tags.push({ label: "tags.likedMe", style: "liked-me" });
  if (swapRequest.mutualInterest) tags.push({ label: "tags.mutualInterest", style: "mutual-interest" });
  return tags;
}

/* ----- Address formatting ----- */
export function formatAddressKey(address: IProperty["address"] | undefined | string): string {
  if (typeof address === "string") return address || "address.notAvailable";

  if (address && typeof address === "object") {
    const streetPart = [address.streetNumber].filter(Boolean).join(" ");
    const cityPart = [address.city, address.postalCode].filter(Boolean).join(" ");
    const formatted = [streetPart, cityPart].filter(part => part.trim().length > 0).join(", ");
    return formatted || "address.notAvailable";
  }

  return "address.notAvailable";
}

/* ----- Feature mapping ----- */
export function mapFeaturesKeys(features: IProperty["features"]) {
  return features.map(featureName => {
    const featureData = homeFeatures.find(f => f.id === featureName);
    if (!featureData) return null;
    return {
      name: featureData.label,
      icon: featureData.emoji,
      style: `feature-${featureName.toLowerCase()}`,
    };
  });
}
